import React from 'react'
import { Box, Button, CircularProgress, Collapse, Typography } from '@mui/material'
import { AppNote, AppValueChip, secondaryButtonSx } from '@components'
import { dsColorNew } from '@palette'
import { Refresh as RefreshIcon } from 'Pages/DsRepricing/@components/icons'
import { formatPreview, headline } from '../@services/previewFormat'

/**
 * "What is due right now, across every rule?" — a question, asked on
 * demand, never a dashboard and never a launch control.
 *
 * Until 2026-10-03 this panel also carried the ▶ that launched a rule,
 * and it only appeared after pressing an eye icon down in the table:
 * two buttons whose meaning depended on a hidden selection. Running a
 * rule now lives on that rule's own row, and its confirmation shows the
 * same figures this panel does (Curro). What is left answers the one
 * thing a row cannot: what the schedule as a whole would launch.
 */
export default function SweepPreview({ preview, rules }) {
	const { raw, loading, error, ruleId, ask } = preview
	const fmt = formatPreview(raw)
	// The panel answers for the whole schedule; a per-rule answer left
	// over from a "Run now" belongs in that dialog, not here.
	const mine = raw && ruleId == null

	return (
		<Box sx={{ px: 2, py: 1.25, mb: 2, borderRadius: '12px', bgcolor: dsColorNew.tableRowAlt,
		           border: `1px solid ${dsColorNew.grayBorder}` }}>
			<Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
				<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, flex: 1 }}>
					Rules run on their own schedule. To see what the engine would
					launch right now across all of them, ask.
				</Typography>
				<Button variant="outlined" startIcon={<RefreshIcon sx={{ fontSize: 14 }}/>} disabled={loading}
				        onClick={() => ask(null)} sx={{ ...secondaryButtonSx, height: 36 }}>
					What is due now?
				</Button>
			</Box>

			{loading && ruleId == null && (
				<Box display="flex" alignItems="center" gap={1} mt={1.5}>
					<CircularProgress size={16}/>
					<Typography component="span" sx={{ fontSize: 13, color: dsColorNew.textMuted }}>Asking the engine…</Typography>
				</Box>
			)}
			{error && ruleId == null && <AppNote severity="error" sx={{ mt: 1.5 }}>{error}</AppNote>}

			<Collapse in={!loading && !error && !!mine}>
				{mine && (
					<Box mt={1.5}>
						<AppNote severity={fmt.refused ? 'warning' : fmt.total > 0 ? 'info' : 'success'}>
							<strong>{headline(fmt)}</strong>
						</AppNote>
						{fmt.rules.map(r => {
							const named = rules.find(x => x.id === r.ruleId)
							return (
								<Box key={r.ruleId} mt={1.5} pl={1} sx={{ borderLeft: '3px solid', borderColor: dsColorNew.grayBorder }}>
									<Typography sx={{ fontSize: 13, fontWeight: 600, color: dsColorNew.textTitle }}>
										{named?.name ?? r.ruleId} — {r.searches} searches over {r.pickupDates} pickup date{r.pickupDates === 1 ? '' : 's'} and {r.offices} office{r.offices === 1 ? '' : 's'}
										{r.reason === 'force' && <AppValueChip sx={{ ml: 1 }}>forced</AppValueChip>}
									</Typography>
									{r.skipped.length > 0 && (
										<Box component="ul" sx={{ m: 0.5, pl: 2.5, color: dsColorNew.textMuted, fontSize: 12 }}>
											{r.skipped.map(s => <li key={s.label}>{s.count} × {s.label}</li>)}
										</Box>
									)}
								</Box>
							)
						})}
					</Box>
				)}
			</Collapse>
		</Box>
	)
}
